import React from "react"
import PropTypes from "prop-types"
import { Column } from "react-virtualized"
import moment from "moment"
import bs from "binary-search"
import DataTable, { tooltipHeaderRenderer } from "@swipesense/data-table"
import { entitiesPropTypes } from "../../utils/propTypes"
import { exposureSearcher } from "../../utils/search"
import { basicSort } from "../../utils/sort"
import CaseBanner from "../CaseBanner"
import Loading from "../Loading"
import "./ExposedVisitList.scss"
require("moment-duration-format") // not called directly, but extends moment to add moment.duration()

const ExposedVisitList = (props) => {
  const isExposureEvent = (visit) => {
    const matchingLocations = props.entities.filter(
      (entity) => entity.id === visit.locationId
    )
    return matchingLocations.length > 0
      ? bs(matchingLocations, visit, exposureSearcher) >= 0
      : false
  }

  const sortedVisits = !props.loading
    ? props.visits
        .map((visit) => ({
          ...visit,
          entryAt: moment.parseZone(visit.entryAt),
        }))
        .sort(basicSort("startAt", "DESC"))
    : []

  const nameCellRenderer = (props) => {
    return (
      <div className="nameCell">
        <div className="name">{props.cellData}</div>
        <div className="subName">{props.rowData.unit}</div>
      </div>
    )
  }

  nameCellRenderer.propTypes = {
    cellData: PropTypes.string,
    rowData: PropTypes.object,
  }

  const visitCellRenderer = (props) => {
    const visitDate = moment.parseZone(props.rowData.entryAt)
    const duration = moment.duration(props.rowData.duration, "seconds")
    return (
      <div className="visit">
        <div className="date">{visitDate.format("MMM D")}</div>
        <div className="dot-divider">•</div>
        <div className="time">{visitDate.format("h:mm a").slice(0, -1)}</div>
        <div className="duration">{duration.format("(h[h] m[m])")}</div>
      </div>
    )
  }

  visitCellRenderer.propTypes = {
    rowData: PropTypes.object,
  }

  const exposureCellRenderer = (props) => {
    return isExposureEvent(props.rowData) ? (
      <div className="exposure-cell">
        <div className="exposure"> !</div>Exposure{" "}
      </div>
    ) : (
      ""
    )
  }

  exposureCellRenderer.propTypes = {
    rowData: PropTypes.object,
  }

  const rowHighlighter = (row) => {
    if (row.index === -1 || !isExposureEvent(sortedVisits[row.index])) {
      return ""
    } else {
      return "exposed-row"
    }
  }

  return (
    <div className="ExposedVisitList">
      <CaseBanner
        caseId={props.caseId}
        employeeName={props.name}
        employeeId={props.employeeId}
        role={props.role}
        department={props.department}
        compliance={props.compliance}
      />
      <div className="visits-wrapper">
        <DataTable
          className="exposed-employee-visit-table"
          rowHeight={46}
          data={sortedVisits}
          loading={props.loading}
          loadingComponent={<Loading />}
          rowClassName={rowHighlighter}
          noRowsMessage={"No visits found."}
        >
          <Column
            key="name"
            dataKey="name"
            width={25}
            label="Unit"
            cellRenderer={nameCellRenderer}
            disableSort={true}
          />
          <Column
            key="entryAt"
            dataKey="entryAt"
            width={45}
            label="Visit Duration"
            cellRenderer={visitCellRenderer}
            disableSort={true}
          />
          <Column
            key="exposureEvent"
            dataKey="exposureEvent"
            width={15}
            label="Exposure Event"
            headerRenderer={tooltipHeaderRenderer(
              '"Exposed" indicates a visit to a room occupied by a contagious patient or employee.'
            )}
            cellRenderer={exposureCellRenderer}
            disableSort={true}
          />
        </DataTable>
      </div>
      <div className="total-results">
        {props.visits && (
          <div>
            {props.visits.length}{" "}
            {props.visits.length === 1 ? "result" : "results"}
          </div>
        )}
      </div>
    </div>
  )
}

ExposedVisitList.propTypes = {
  caseId: PropTypes.string.isRequired,
  name: PropTypes.string.isRequired,
  employeeId: PropTypes.string,
  role: PropTypes.string,
  department: PropTypes.string,
  compliance: PropTypes.number,
  loading: PropTypes.bool,
  modalOpen: PropTypes.bool,
  entities: entitiesPropTypes,
  visits: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string,
      duration: PropTypes.number,
      unit: PropTypes.string,
      name: PropTypes.string,
      entryAt: PropTypes.string,
      exitAt: PropTypes.string,
    })
  ),
}

ExposedVisitList.defaultProps = {
  caseId: "",
  entities: [],
}

export default ExposedVisitList
